import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Icon } from "./Icon";
import { motion } from "framer-motion";
import { AnimationOverlays } from "./AnimationOverlays";
import { useMediaShowcase } from "../hooks/useMediaShowcase";
import type {
  MediaShowcaseData,
  MediaShowcaseContent,
  OverlayConfig,
} from "../types/narrative";
import { getResponsiveSrcSet } from "../utils/responsiveImage";
import { ANIMATION_DURATIONS } from "../constants/animations";
import { useLottieReducedMotion } from "../hooks/useLottieReducedMotion";

function isBadgeItem(item: MediaShowcaseContent): boolean {
  if (item.display.type !== "image") return false;
  const filename = item.shareAsset?.filename ?? "";
  return /badge/i.test(filename) || /badge/i.test(item.alt ?? "");
}

interface MediaShowcaseProps {
  data: MediaShowcaseData;
}

/**
 * Filter overlays based on slide active state.
 * Viewport-relative overlays only render on the active slide to prevent
 * offscreen slides from showing full-screen animations.
 */
function getActiveOverlay(
  overlay: OverlayConfig | OverlayConfig[] | undefined,
  isActive: boolean,
): OverlayConfig | OverlayConfig[] | undefined {
  if (!overlay) return undefined;
  if (isActive) return overlay;

  const overlays = Array.isArray(overlay) ? overlay : [overlay];
  const filtered = overlays.filter((o) => o.relativeTo !== "viewport");
  if (filtered.length === 0) return undefined;
  return filtered.length === 1 ? filtered[0] : filtered;
}

function MediaItem({
  item,
  index,
  isActive,
}: {
  item: MediaShowcaseContent;
  index: number;
  isActive: boolean;
}) {
  const { autoplay, prefersReducedMotion, dotLottieRefCallback } =
    useLottieReducedMotion();
  const activeOverlay = getActiveOverlay(item.display.overlay, isActive);
  const responsiveInfo =
    item.display.type === "image"
      ? getResponsiveSrcSet(item.display.src)
      : null;
  const isBadge = isBadgeItem(item);

  const displayContent =
    item.display.type === "image" ? (
      responsiveInfo ? (
        <picture>
          <source
            type="image/webp"
            srcSet={responsiveInfo.srcSet}
            sizes="min(100vw, 400px)"
          />
          <img
            src={responsiveInfo.fallbackSrc}
            alt={item.alt || item.label || ""}
            className="w-full h-auto max-h-[50vh] rounded-xl object-contain"
            draggable={false}
          />
        </picture>
      ) : (
        <img
          src={item.display.src}
          alt={item.alt || item.label || ""}
          className="w-full h-auto max-h-[50vh] rounded-xl object-contain"
          draggable={false}
        />
      )
    ) : (
      <DotLottieReact
        src={item.display.src}
        autoplay={autoplay}
        loop={!prefersReducedMotion}
        dotLottieRefCallback={dotLottieRefCallback}
        className="w-full h-auto max-h-[50vh] rounded-xl"
      />
    );

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Display content with optional overlay */}
      <div className="relative w-full">
        {isBadge ? (
          <div className="badge-wrap">
            <div className="badge-inner">{displayContent}</div>
          </div>
        ) : (
          displayContent
        )}

        {/* Composited overlays on top of display content */}
        {activeOverlay && (
          <div className="absolute inset-0 pointer-events-none">
            <AnimationOverlays
              overlay={activeOverlay}
              keyPrefix={`media-overlay-${index}`}
            />
          </div>
        )}
      </div>

      {/* Caption */}
      {item.label && (
        <p className="text-white/70 text-sm text-center font-light">
          {item.label}
        </p>
      )}
    </div>
  );
}

export function MediaShowcase({ data }: MediaShowcaseProps) {
  const {
    activeIndex,
    isCarousel,
    scrollContainerRef,
    scrollToIndex,
    showDownload,
    showShare,
    showDownloadAll,
    isDownloading,
    isSharing,
    isDownloadingAll,
    handleDownload,
    handleShare,
    handleDownloadAll,
  } = useMediaShowcase({ data });

  return (
    <div className="relative w-full max-w-[400px] mx-auto flex flex-col gap-3">
      {/* Content area */}
      {isCarousel ? (
        <>
          {/* Carousel */}
          <div
            ref={scrollContainerRef}
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {data.items.map((item, i) => (
              <div
                key={i}
                data-index={i}
                className="w-full flex-shrink-0 snap-center"
              >
                <MediaItem item={item} index={i} isActive={i === activeIndex} />
              </div>
            ))}
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2">
            {data.items.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToIndex(i)}
                aria-label={`Go to item ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === activeIndex
                    ? "bg-white"
                    : "bg-white/30 hover:bg-white/50"
                }`}
              />
            ))}
          </div>

          {/* Desktop arrows */}
          {activeIndex > 0 && (
            <button
              onClick={() => scrollToIndex(activeIndex - 1)}
              aria-label="Previous item"
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full items-center justify-center w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
            >
              ‹
            </button>
          )}
          {activeIndex < data.items.length - 1 && (
            <button
              onClick={() => scrollToIndex(activeIndex + 1)}
              aria-label="Next item"
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-full items-center justify-center w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
            >
              ›
            </button>
          )}
        </>
      ) : (
        /* Single item */
        <div className="w-full">
          <MediaItem item={data.items[0]} index={0} isActive={true} />
        </div>
      )}

      {/* Action buttons — fade in after media entrance animation */}
      {(showDownload || showShare) && (
        <motion.div
          className="flex items-center justify-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            delay: ANIMATION_DURATIONS.normal / 1000 + 0.3,
            duration: 0.4,
          }}
        >
          {showShare && (
            <ActionButton
              onClick={() => handleShare()}
              disabled={isSharing}
              loading={isSharing}
              icon="arrow-up-from-bracket"
              label={data.shareLabel || "Share"}
              emphasis
            />
          )}
          {showDownload && (
            <ActionButton
              onClick={() => handleDownload()}
              disabled={isDownloading}
              loading={isDownloading}
              icon="arrow-down-to-bracket"
              label={data.downloadLabel || "Download"}
            />
          )}
        </motion.div>
      )}
      {showDownloadAll && (
        <motion.div
          className="flex justify-center mt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            delay: ANIMATION_DURATIONS.normal / 1000 + 0.3,
            duration: 0.4,
          }}
        >
          <ActionButton
            onClick={handleDownloadAll}
            disabled={isDownloadingAll}
            loading={isDownloadingAll}
            icon="cloud-arrow-down"
            label="Download All"
          />
        </motion.div>
      )}
    </div>
  );
}

function ActionButton({
  onClick,
  disabled,
  loading,
  icon,
  label,
  emphasis,
}: {
  onClick: () => void;
  disabled: boolean;
  loading: boolean;
  icon?: string;
  label: string;
  emphasis?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-2 px-4 py-3 text-sm rounded-full font-medium shadow-lg cursor-pointer backdrop-blur-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] ${
        emphasis
          ? "bg-emerald-500/80 hover:bg-emerald-400/90 text-white border-emerald-400/40"
          : "bg-white/10 hover:bg-white/20 text-white border-white/20"
      }`}
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      ) : (
        icon && <Icon icon={icon} />
      )}
      {label}
    </button>
  );
}
